import { readAgentJourney } from "./agentJourneyView";
import type {
  WizardIntake,
  WizardStepIndex,
  MergedDrawing,
  CatalogChoice,
} from "@/modules/quickquote/aiquote/wizardTypes";
import type { MultiBodyDoc } from "@/modules/quickquote/aiquote/MultiBodyOffer";
import type {
  RecognizeDrawingResult,
  DrawingProcessJudgment,
  CatalogPick,
} from "@/api/factorQuotes";
import type {
  JourneyPage,
  JourneyJson,
  JourneySnapshot,
} from "./quoteJourneyState";
import { safeQuoteOrigin } from "./quoteJourneyState";
import { readLegacyJourney } from "./legacyQuoteJourney";
import {
  CUSTOM_MATERIAL_CLASSES,
  type CustomMaterialDraft,
} from "../customMaterial";
import { readProjectJourney } from "@/modules/quoteproject/projectJourneyView";
import {
  isFieldRawArray,
  readWorksheetJourney,
  type FieldRaw,
} from "../worksheetJourneyView";
import { quoteListDateValid } from "@/modules/quickquote/quoteListView";
export type QuickJourneyView = {
  statusFilter: string;
  query?: string;
  listOffset?: number;
  dateFrom?: string;
  dateTo?: string;
  listKind: "quotes" | "projects";
  selectedId: string | null;
  factorDraftId: string | null;
  creating: boolean;
  createMode: "factor" | "part" | "rfq";
  setupDrawer: boolean;
};
export type WizardJourneyView = {
  factorRaw?: FieldRaw[];
  /** 第一页「自己填材料」表单没交的原文(去设置回来照原文开着);没有 / 没改动 → null 或不带 */
  customMaterialForm?: CustomMaterialDraft | null;
  intake: WizardIntake;
  draftId: string | null;
  step: WizardStepIndex;
  assemblyDoc: { id: string; name: string } | null;
  multiBodyDoc: MultiBodyDoc | null;
  mergedDrawing: MergedDrawing | null;
  pickedPage: number | null;
  wholeAsOne: boolean;
  wholeHints: RecognizeDrawingResult | null;
  recognition: RecognizeDrawingResult | "failed" | null;
  judgment: { docId: string; result: DrawingProcessJudgment | null } | null;
  materialHint: string | null;
  materialMatch: CatalogChoice | null;
  materialWhy: string | null;
  materialNear: CatalogPick[];
  surfaceHint: string | null;
  surfaceItems: CatalogChoice[];
  heatTreatItems: CatalogChoice[];
  logistics: {
    shipping: CatalogChoice | null;
    packaging: CatalogChoice | null;
    inspection: CatalogChoice | null;
  };
};
export type FromPartJourneyView = {
  q: string;
  partId: string | null;
  qty: number | string;
  tolerance: string;
  uploadMachineId: string | null;
  ncMaterial: string;
  ncStockMass: number | string;
  ncRapid: number | string;
  ncToolChange: number | string;
  ncOverride: number | string;
  paramsOpen: boolean;
};
export type RfqJourneyView = {
  intakeId: string | null;
  mode: "turnkey" | "labor_only";
  excluded: import("@/api/quotes").RfqOptionalKey[];
  linkCustomer: boolean;
  matchedCustomerId: string | null;
  reused: string | null;
  process: CatalogChoice | null;
  cad: { id: string; name: string } | null;
  drawing: { id: string; name: string } | null;
  edits: { material: string; tolerance: string; qty: string };
};
export function readFromPartJourney(
  page: JourneyPage | null,
): FromPartJourneyView | null {
  if (page?.key !== "frompart" || page.version !== 1 || page.files.length)
    return null;
  const v = page.data;
  if (
    typeof v.q !== "string" ||
    !nullableId(v.partId) ||
    !(typeof v.qty === "string" ? v.qty.length <= 100 : positive(v.qty)) ||
    !["coarse", "standard", "precision", "ultra"].includes(
      String(v.tolerance),
    ) ||
    !nullableId(v.uploadMachineId) ||
    typeof v.ncMaterial !== "string" ||
    typeof v.paramsOpen !== "boolean" ||
    ![v.ncStockMass, v.ncRapid, v.ncToolChange, v.ncOverride].every(
      (x) =>
        (typeof x === "string" && x.length <= 100) ||
        (typeof x === "number" && Number.isFinite(x)),
    )
  )
    return null;
  return v as unknown as FromPartJourneyView;
}
export function readRfqJourney(
  page: JourneyPage | null,
): RfqJourneyView | null {
  if (page?.key !== "rfq" || page.version !== 1 || page.files.length)
    return null;
  const v = page.data;
  const documentRef = (x: unknown) =>
    x === null || (obj(x) && uuid(x.id) && typeof x.name === "string");
  if (
    !nullableId(v.intakeId) ||
    !["turnkey", "labor_only"].includes(String(v.mode)) ||
    !Array.isArray(v.excluded) ||
    !v.excluded.every((x) =>
      ["surface", "customer", "requested_due", "drawing_no"].includes(
        String(x),
      ),
    ) ||
    typeof v.linkCustomer !== "boolean" ||
    !nullableId(v.matchedCustomerId) ||
    (v.linkCustomer && v.matchedCustomerId === null) ||
    !nullableId(v.reused) ||
    !choice(v.process) ||
    !documentRef(v.cad) ||
    !documentRef(v.drawing) ||
    !obj(v.edits) ||
    typeof v.edits.material !== "string" ||
    typeof v.edits.qty !== "string" ||
    !["", "coarse", "standard", "precision", "ultra"].includes(
      String(v.edits.tolerance),
    )
  )
    return null;
  return v as unknown as RfqJourneyView;
}
export function journeyViewData(
  value:
    | QuickJourneyView
    | WizardJourneyView
    | FromPartJourneyView
    | RfqJourneyView,
): Record<string, JourneyJson> {
  return JSON.parse(JSON.stringify(value));
}
const uuid = (v: unknown): v is string =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const obj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const text = (v: unknown) => v === null || typeof v === "string";
const nullableId = (v: unknown) => v === null || uuid(v);
const choice = (v: unknown) =>
  v === null ||
  (obj(v) && (uuid(v.id) || v.id === "custom") && typeof v.label === "string");
const choices = (v: unknown) =>
  Array.isArray(v) && v.every((item) => item !== null && choice(item));
const positive = (v: unknown): v is number =>
  typeof v === "number" && Number.isSafeInteger(v) && v > 0;
const recordOrNull = (v: unknown) => v === null || obj(v);
const CUSTOM_DRAFT_KEYS = [
  "name",
  "material_class",
  "density_g_cm3",
  "unit_price_per_kg",
  "scrap_unit_price_per_kg",
] as const;
/** 自己填材料表单原文:恰好五个字符串键、每个 ≤ 200 字,类别只能是空或材质类别表里的值 */
const customMaterialDraft = (v: unknown): v is CustomMaterialDraft =>
  obj(v) &&
  Object.keys(v).length === CUSTOM_DRAFT_KEYS.length &&
  CUSTOM_DRAFT_KEYS.every(
    (key) => typeof v[key] === "string" && (v[key] as string).length <= 200,
  ) &&
  (v.material_class === "" ||
    CUSTOM_MATERIAL_CLASSES.some((c) => c.value === v.material_class));
export function readQuickJourney(
  page: JourneyPage | null,
): QuickJourneyView | null {
  if (page?.key !== "quickquote" || page.version !== 1) return null;
  const v = page.data;
  if (
    typeof v.statusFilter !== "string" ||
    ![
      "all",
      "needs_action",
      "draft",
      "pending_review",
      "approved",
      "rejected",
    ].includes(v.statusFilter) ||
    (v.query !== undefined &&
      (typeof v.query !== "string" || v.query.length > 200)) ||
    (v.listOffset !== undefined &&
      (typeof v.listOffset !== "number" ||
        !Number.isSafeInteger(v.listOffset) ||
        v.listOffset < 0 ||
        v.listOffset % 100 !== 0)) ||
    (v.dateFrom !== undefined &&
      (typeof v.dateFrom !== "string" ||
        (v.dateFrom !== "" && !quoteListDateValid(v.dateFrom)))) ||
    (v.dateTo !== undefined &&
      (typeof v.dateTo !== "string" ||
        (v.dateTo !== "" && !quoteListDateValid(v.dateTo)))) ||
    (typeof v.dateFrom === "string" &&
      typeof v.dateTo === "string" &&
      v.dateFrom !== "" &&
      v.dateTo !== "" &&
      v.dateFrom > v.dateTo) ||
    !["quotes", "projects"].includes(String(v.listKind)) ||
    !nullableId(v.selectedId) ||
    !nullableId(v.factorDraftId) ||
    typeof v.creating !== "boolean" ||
    !["factor", "part", "rfq"].includes(String(v.createMode)) ||
    typeof v.setupDrawer !== "boolean"
  )
    return null;
  if (
    v.factorDraftId !== null &&
    (v.selectedId !== v.factorDraftId || v.creating)
  )
    return null;
  return v as unknown as QuickJourneyView;
}
export function readWizardJourney(
  page: JourneyPage | null,
): WizardJourneyView | null {
  if (page?.key !== "wizard" || page.version !== 1) return null;
  const v = page.data,
    i = v.intake;
  if (v.factorRaw !== undefined && !isFieldRawArray(v.factorRaw)) return null;
  if (
    v.customMaterialForm !== undefined &&
    v.customMaterialForm !== null &&
    !customMaterialDraft(v.customMaterialForm)
  )
    return null;
  if (
    !obj(i) ||
    !nullableId(i.cadDocId) ||
    !nullableId(i.drawingDocId) ||
    !text(i.cadName) ||
    !text(i.drawingName) ||
    !["turnkey", "labor_only"].includes(String(i.mode)) ||
    !["coarse", "standard", "precision", "ultra"].includes(
      String(i.tolerance),
    ) ||
    !(typeof i.qty === "string" ? i.qty.length <= 100 : positive(i.qty)) ||
    !choice(i.material) ||
    !choices(i.processes) ||
    !choice(i.workstation) ||
    !choice(i.customer)
  )
    return null;
  if (
    i.toleranceSource !== undefined &&
    !["default", "user", "drawing"].includes(String(i.toleranceSource))
  )
    return null;
  if (
    i.materialSource !== undefined &&
    !["drawing", "manual"].includes(String(i.materialSource))
  )
    return null;
  if (
    i.processSource !== undefined &&
    !["drawing", "manual", "stale", "kept"].includes(String(i.processSource))
  )
    return null;
  if (
    i.customMaterial !== undefined &&
    (!obj(i.customMaterial) || typeof i.customMaterial.name !== "string")
  )
    return null;
  if (
    !nullableId(v.draftId) ||
    ![0, 1, 2, 3].includes(v.step as number) ||
    (v.step !== 0 && v.draftId === null) ||
    typeof v.wholeAsOne !== "boolean" ||
    !(v.pickedPage === null || positive(v.pickedPage))
  )
    return null;
  if (
    v.assemblyDoc !== null &&
    (!obj(v.assemblyDoc) ||
      !uuid(v.assemblyDoc.id) ||
      typeof v.assemblyDoc.name !== "string")
  )
    return null;
  if (
    v.multiBodyDoc !== null &&
    (!obj(v.multiBodyDoc) ||
      !uuid(v.multiBodyDoc.id) ||
      typeof v.multiBodyDoc.name !== "string" ||
      !positive(v.multiBodyDoc.bodies))
  )
    return null;
  if (v.mergedDrawing !== null) {
    const m = v.mergedDrawing;
    if (
      !obj(m) ||
      !uuid(m.docId) ||
      typeof m.fileName !== "string" ||
      !Array.isArray(m.parts) ||
      !m.parts.every(
        (p) =>
          obj(p) &&
          typeof p.name === "string" &&
          Array.isArray(p.pages) &&
          p.pages.every(positive),
      ) ||
      !Array.isArray(m.unnamedPages) ||
      !m.unnamedPages.every(positive)
    )
      return null;
  }
  if (
    !recordOrNull(v.wholeHints) ||
    !(v.recognition === "failed" || recordOrNull(v.recognition)) ||
    !(
      v.judgment === null ||
      (obj(v.judgment) &&
        uuid(v.judgment.docId) &&
        recordOrNull(v.judgment.result))
    )
  )
    return null;
  if (
    !text(v.materialHint) ||
    !choice(v.materialMatch) ||
    !text(v.materialWhy) ||
    !text(v.surfaceHint) ||
    !Array.isArray(v.materialNear) ||
    !v.materialNear.every(
      (p) => obj(p) && uuid(p.id) && typeof p.name_zh === "string",
    ) ||
    !choices(v.surfaceItems) ||
    !choices(v.heatTreatItems)
  )
    return null;
  if (
    !obj(v.logistics) ||
    !choice(v.logistics.shipping) ||
    !choice(v.logistics.packaging) ||
    !choice(v.logistics.inspection)
  )
    return null;
  if (page.files.some((f, index) => f.slot !== `loose-${index}`)) return null;
  return v as unknown as WizardJourneyView;
}
export function validQuoteJourneyViews(snapshot: JourneySnapshot): boolean {
  const origin = safeQuoteOrigin(snapshot.origin);
  if (!origin) return false;
  if (new Set(snapshot.pages.map((p) => p.key)).size !== snapshot.pages.length)
    return false;
  const pathname = new URL(origin, "https://quote.invalid").pathname;
  if (pathname === "/cnc/quotes")
    return (
      snapshot.pages.length === 1 &&
      Boolean(readLegacyJourney(snapshot.pages[0]))
    );
  const worksheetPage = snapshot.pages.find((p) => p.key === "worksheet");
  const worksheet = worksheetPage ? readWorksheetJourney(worksheetPage) : null;
  if (worksheetPage && !worksheet) return false;
  if (pathname === "/app/agent") {
    const session = new URL(origin, "https://quote.invalid").searchParams.get(
      "s",
    );
    const agent = readAgentJourney(
      snapshot.pages.find((page) => page.key === "agent") ?? null,
      session,
    );
    return (
      !!agent &&
      (worksheet
        ? snapshot.pages.length === 2 &&
          worksheet.owner.kind === "chat-card" &&
          worksheet.owner.sessionId === agent.sessionId
        : snapshot.pages.length === 1)
    );
  }
  if (/^\/(?:cnc\/quote-projects|app\/quickquote\/projects)\//.test(pathname)) {
    const project = readProjectJourney(
      snapshot.pages.find((p) => p.key === "project") ?? null,
      pathname.split("/").at(-1),
    );
    if (!project) return false;
    if (!worksheet) return snapshot.pages.length === 1;
    return (
      snapshot.pages.length === 2 &&
      worksheet.owner.kind === "project-line" &&
      worksheet.owner.projectId === project.projectId &&
      worksheet.owner.lineId === project.openLine?.lineId &&
      worksheet.quoteId === project.openLine?.quoteId
    );
  }
  if (pathname !== "/app/quickquote") return false;
  const quick = readQuickJourney(
    snapshot.pages.find((p) => p.key === "quickquote") ?? null,
  );
  if (!quick) return false;
  if (!quick.creating) {
    if (!worksheet) return snapshot.pages.length === 1;
    return (
      snapshot.pages.length === 2 &&
      worksheet.owner.kind === "quickquote" &&
      worksheet.quoteId === quick.factorDraftId
    );
  }
  const key = { factor: "wizard", part: "frompart", rfq: "rfq" }[
    quick.createMode
  ];
  if (
    snapshot.pages.length !== (worksheet ? 3 : 2) ||
    snapshot.pages.some(
      (p) => p.key !== "quickquote" && p.key !== key && p.key !== "worksheet",
    )
  )
    return false;
  const page = snapshot.pages.find((p) => p.key === key) ?? null;
  if (key === "wizard") {
    const wizard = readWizardJourney(page);
    return Boolean(
      wizard &&
      (!worksheet ||
        (wizard.step === 3 &&
          worksheet.owner.kind === "wizard" &&
          worksheet.quoteId === wizard.draftId)),
    );
  }
  if (worksheet) return false;
  return Boolean(
    key === "frompart" ? readFromPartJourney(page) : readRfqJourney(page),
  );
}

export function keepRestoredDrawing(
  initial: Pick<
    WizardIntake,
    "drawingDocId" | "tolerance" | "toleranceSource"
  > | null,
  current: Pick<WizardIntake, "drawingDocId" | "tolerance" | "toleranceSource">,
): boolean {
  return Boolean(
    initial &&
    initial.drawingDocId === current.drawingDocId &&
    initial.tolerance === current.tolerance &&
    initial.toleranceSource === current.toleranceSource,
  );
}
